<script setup lang="ts">
import { computed } from 'vue';
import { useUtilities } from '@apparatix/composables';
import Skeleton from 'primevue/skeleton';

type SkeletonShape = 'square' | 'rounded' | 'circle'

type SkeletonRow = {
  columns?: number;
  shape?: SkeletonShape;
  compact?: boolean;
  height?: string;
}

type Props = {
  loading: boolean;
  rows?: number | SkeletonRow[];
  fadeAfterLoad?: boolean;
  justifyAround?: boolean;
  justifyBetween?: boolean;
} & SkeletonRow;

const props = defineProps<Props>();

const defaultRow: SkeletonRow = {
  columns: 3,
  shape: 'rounded',
  compact: false,
  height: props.compact ? '1rem' : '2rem',
};

const mergeDefaultOptions = (defaults: SkeletonRow, provided: SkeletonRow): SkeletonRow => Object.fromEntries(
  Object.keys(defaults).map(key => [key, provided[key] !== undefined ? provided[key] : defaults[key]]),
);

const { arrayHasValues } = useUtilities();

const rowsBase = computed<SkeletonRow[]>(() => {
  const partialProps = { ...props };
  delete partialProps.loading;
  delete partialProps.rows;
  delete partialProps.fadeAfterLoad;

  if (typeof props.rows === 'number') {
    return Array(props.rows).fill(mergeDefaultOptions(defaultRow, partialProps));
  }

  if (arrayHasValues(props.rows)) {
    return props.rows.map(row => mergeDefaultOptions(defaultRow, row));
  }

  return Array(6).fill(mergeDefaultOptions(defaultRow, partialProps));
});

const keyedRows = computed<Array<SkeletonRow & { key: string }>>(() => rowsBase.value.map(row => ({ ...row, key: crypto.randomUUID() })));

const getBorderRadiusClass = (shape: SkeletonShape, compact: boolean) => {
  switch (shape) {
    case 'square':
      return undefined;
    case 'rounded':
      return compact ? '!rounded-md' : '!rounded-lg';
    case 'circle':
      return '!rounded-full';
    default:
      return undefined;
  }
};
</script>
<template>
  <transition name="fade" :mode="props.fadeAfterLoad ? 'out-in' : 'default'">
    <slot v-if="!props.loading" />
    <div v-else class="flex flex-col h-full" :class="{'gap-3 md:gap-4 lg:gap=6': !props.compact, 'gap-2 lg:gap-4': props.compact, 'justify-around': props.justifyAround, 'justify-between': props.justifyBetween}">
      <div
        v-for="row in keyedRows"
        :key="row.key"
        class="flex w-full"
        :class="{'gap-1 lg:gap-2': row.compact, 'gap-2 lg:gap-4': !row.compact, 'justify-between': row.shape === 'circle'}"
      >
        <Skeleton
          v-for="i in row.columns"
          :key="i"
          :class="[getBorderRadiusClass(row.shape, row.compact), row.shape === 'circle' ? 'flex' : 'flex-1']"
          :style="{height: row.height, width: row.shape === 'circle' ? row.height : undefined}"
        />
      </div>
    </div>
  </transition>
</template>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.75s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
